/**
 * AdminPage Component
 * Central administration hub with tabbed navigation
 */

import React from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Users,
  Building2,
  UsersRound,
  Shield,
  Calendar,
  Settings,
  ChevronRight
} from 'lucide-react';
import { clsx } from 'clsx';
import { useAuth } from '../../context/AuthContext';
import { Card, Button, LoadingSpinner } from '../../components/common';

// Tab components (will be created separately)
import { UsersTab } from './tabs/UsersTab';
import { DepartmentsTab } from './tabs/DepartmentsTab';
import { TeamsTab } from './tabs/TeamsTab';
import { PermissionsTab } from './tabs/PermissionsTab';
import { CyclesTab } from './tabs/CyclesTab';
import { SettingsTab } from './tabs/SettingsTab';

type AdminTab = 'users' | 'departments' | 'teams' | 'permissions' | 'cycles' | 'settings';

interface TabConfig {
  id: AdminTab;
  name: string;
  icon: React.ElementType;
  description: string;
}

const tabs: TabConfig[] = [
  {
    id: 'users',
    name: 'Users',
    icon: Users,
    description: 'Manage users, invite new members, and assign roles'
  },
  {
    id: 'departments',
    name: 'Departments',
    icon: Building2,
    description: 'Create and manage organizational departments'
  },
  {
    id: 'teams',
    name: 'Teams',
    icon: UsersRound,
    description: 'Manage cross-departmental teams'
  },
  {
    id: 'permissions',
    name: 'Permissions',
    icon: Shield,
    description: 'Configure role-based access controls'
  },
  {
    id: 'cycles',
    name: 'OKR Cycles',
    icon: Calendar,
    description: 'Manage OKR planning cycles and timelines'
  },
  {
    id: 'settings',
    name: 'Settings',
    icon: Settings,
    description: 'Organization settings and preferences'
  }
];

export const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // The tab lives in `?tab=`, through the router, like Reports and Check-ins. It used to be a
  // `#hash` written with raw `window.history.replaceState`, which the router never saw: the
  // Users tab's filters (also in the URL) then wiped the hash on every keystroke. An old
  // `#tab` bookmark is still honoured.
  const isTab = (id: string | null): id is AdminTab => !!id && tabs.some((t) => t.id === id);
  const fromQuery = searchParams.get('tab');
  const fromHash = location.hash.replace('#', '');
  const activeTab: AdminTab = isTab(fromQuery) ? fromQuery : isTab(fromHash) ? fromHash : 'users';

  // Check if user has admin access
  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (user.role !== 'account_admin' && user.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Card className="p-8 max-w-md">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-danger-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-danger-600" />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Access Denied</h2>
            <p className="text-stone-600 mb-6">
              Only administrators can access the admin panel.
            </p>
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Only `tab` changes; `replace`, as before, so switching tabs adds no Back step. Each tab's
  // own filters keep their own keys.
  const handleTabChange = (tabId: AdminTab) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tabId);
        return next;
      },
      { replace: true }
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'users':
        return <UsersTab />;
      case 'departments':
        return <DepartmentsTab />;
      case 'teams':
        return <TeamsTab />;
      case 'permissions':
        return <PermissionsTab />;
      case 'cycles':
        return <CyclesTab />;
      case 'settings':
        return <SettingsTab />;
      default:
        return <UsersTab />;
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header - Warm gradient */}
      <div className="bg-gradient-to-r from-stone-800 via-stone-700 to-primary-600">
        <div className="px-6 lg:px-8 py-8">
          <div className="flex items-center gap-2 text-sm text-stone-300 mb-2">
            <span>Admin</span>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium">{tabs.find(t => t.id === activeTab)?.name}</span>
          </div>
          <h1 className="text-3xl font-semibold text-white" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Administration</h1>
          <p className="mt-1 text-sm text-stone-300">
            Manage your organization's settings, users, and permissions
          </p>
        </div>
      </div>

      <div className="px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Tab Navigation - Compact sidebar */}
          <div className="lg:w-56 flex-shrink-0">
            <nav className="space-y-1 lg:sticky lg:top-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={clsx(
                      'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all',
                      isActive
                        ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-500'
                        : 'text-stone-700 hover:bg-sand border-l-4 border-transparent'
                    )}
                  >
                    <Icon
                      className={clsx(
                        'w-5 h-5 flex-shrink-0',
                        isActive ? 'text-primary-500' : 'text-stone-400'
                      )}
                    />
                    <div className="min-w-0">
                      <p className={clsx(
                        'font-medium truncate',
                        isActive ? 'text-primary-700' : 'text-stone-900'
                      )}>
                        {tab.name}
                      </p>
                      <p className="text-xs text-stone-500 truncate hidden lg:block">
                        {tab.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content - Full width */}
          <div className="flex-1 min-w-0">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};
